/**
 * Composant GHS Pictogram
 * Affiche les pictogrammes de danger selon le Système Général Harmonisé (SGH/GHS)
 * avec tooltip interactif au hover/click
 */
import { useState } from 'react'

interface GHSPictogramProps {
  code: string
  name?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  showTooltip?: boolean
}

// Données des pictogrammes GHS
const GHS_DATA: Record<string, { name: string; description: string }> = {
  GHS01: {
    name: 'Explosif',
    description: 'Explosifs, substances auto-réactives, peroxydes organiques',
  },
  GHS02: {
    name: 'Inflammable',
    description: 'Gaz, aérosols, liquides, solides inflammables',
  },
  GHS03: {
    name: 'Comburant',
    description: 'Gaz, liquides, solides comburants',
  },
  GHS04: {
    name: 'Gaz sous pression',
    description: 'Gaz comprimé, liquéfié, dissous',
  },
  GHS05: {
    name: 'Corrosif',
    description: 'Corrosif pour les métaux, la peau, les yeux',
  },
  GHS06: {
    name: 'Toxique',
    description: 'Toxicité aiguë (mortel ou toxique)',
  },
  GHS07: {
    name: 'Nocif / Irritant',
    description: 'Irritation cutanée/oculaire, sensibilisation cutanée, toxicité aiguë légère',
  },
  GHS08: {
    name: 'Danger pour la santé',
    description: 'CMR, toxicité pour organes cibles, sensibilisation respiratoire',
  },
  GHS09: {
    name: 'Danger environnement',
    description: 'Dangereux pour l\'environnement aquatique',
  },
}

// SVGs optimisés des pictogrammes GHS (losange rouge/blanc)
const GHS_SVGS: Record<string, JSX.Element> = {
  GHS01: (
    // Bombe explosant
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        <circle cx="50" cy="55" r="12" />
        <path d="M42,43 L38,25 L45,35 L50,20 L55,35 L62,25 L58,43" />
        <rect x="48" y="40" width="4" height="8" />
      </g>
    </svg>
  ),
  GHS02: (
    // Flamme
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <path
        d="M50,20 C50,20 65,35 65,50 C65,55 63,60 58,65 C60,58 55,52 50,52 C45,52 40,58 42,65 C37,60 35,55 35,50 C35,35 50,20 50,20 Z"
        fill="#000"
      />
    </svg>
  ),
  GHS03: (
    // Flamme sur cercle (comburant)
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <circle cx="50" cy="60" r="15" fill="none" stroke="#000" strokeWidth="3" />
      <path
        d="M50,28 C50,28 62,38 62,48 C62,52 60,55 56,58 C58,52 54,48 50,48 C46,48 42,52 44,58 C40,55 38,52 38,48 C38,38 50,28 50,28 Z"
        fill="#000"
      />
    </svg>
  ),
  GHS04: (
    // Bouteille de gaz
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <path
        d="M40,30 L40,25 C40,22 43,20 50,20 C57,20 60,22 60,25 L60,30 C65,32 68,40 68,55 C68,70 62,78 50,78 C38,78 32,70 32,55 C32,40 35,32 40,30 Z"
        fill="#000"
      />
    </svg>
  ),
  GHS05: (
    // Corrosion
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        {/* Main avec gouttes */}
        <path d="M30,35 L40,35 L38,50 L45,50 L42,65 L48,65 L45,75 L30,75 L33,65 L28,65 L32,50 L25,50 Z" />
        {/* Surface corrodée */}
        <path d="M55,55 L75,55 L73,60 L70,58 L68,62 L65,58 L62,62 L60,58 L57,60 Z" />
        <path d="M55,65 L75,65 L75,75 L55,75 Z" />
        {/* Éprouvette */}
        <rect x="58" y="30" width="12" height="25" rx="2" />
        <path d="M60,40 L68,40 L68,50 L60,50 Z" fill="#fff" />
      </g>
    </svg>
  ),
  GHS06: (
    // Tête de mort
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        {/* Crâne */}
        <ellipse cx="50" cy="40" rx="20" ry="18" />
        <ellipse cx="42" cy="38" rx="5" ry="6" fill="#fff" />
        <ellipse cx="58" cy="38" rx="5" ry="6" fill="#fff" />
        <path d="M45,48 L50,52 L55,48" stroke="#fff" strokeWidth="2" fill="none" />
        {/* Os croisés */}
        <path d="M28,60 C28,58 30,58 32,60 L50,72 L68,60 C70,58 72,58 72,60 C72,62 70,64 68,64 L55,72 L68,80 C70,82 70,84 68,84 C66,84 64,82 62,80 L50,72 L38,80 C36,82 34,84 32,84 C30,84 30,82 32,80 L45,72 L32,64 C30,64 28,62 28,60 Z" />
      </g>
    </svg>
  ),
  GHS07: (
    // Point d'exclamation
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        <rect x="45" y="25" width="10" height="35" rx="3" />
        <circle cx="50" cy="72" r="6" />
      </g>
    </svg>
  ),
  GHS08: (
    // Silhouette santé
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        {/* Torse humain */}
        <circle cx="50" cy="28" r="8" />
        <path d="M35,40 L40,40 L40,55 L35,75 L42,75 L50,60 L58,75 L65,75 L60,55 L60,40 L65,40 L65,50 L70,55 L70,45 L60,38 L40,38 L30,45 L30,55 L35,50 Z" />
        {/* Étoile de danger */}
        <path d="M50,45 L52,50 L58,50 L53,54 L55,60 L50,56 L45,60 L47,54 L42,50 L48,50 Z" fill="#fff" />
      </g>
    </svg>
  ),
  GHS09: (
    // Environnement (arbre + poisson)
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#ff0000" strokeWidth="4" />
      <g fill="#000">
        {/* Arbre */}
        <path d="M30,65 L35,55 L32,55 L38,45 L35,45 L42,32 L49,45 L46,45 L52,55 L49,55 L54,65 Z" />
        <rect x="40" y="65" width="4" height="10" />
        {/* Poisson mort */}
        <path d="M55,55 C55,55 52,60 55,65 C55,65 60,70 70,65 L75,70 L75,60 L70,65 C70,65 65,60 65,60 C65,60 70,55 65,55 C65,55 60,50 55,55 Z" />
        <circle cx="60" cy="60" r="2" fill="#fff" />
        {/* Vagues */}
        <path d="M25,75 Q32,72 40,75 Q48,78 55,75 Q62,72 70,75" stroke="#000" strokeWidth="2" fill="none" />
      </g>
    </svg>
  ),
}

// Pictogramme par défaut (point d'interrogation)
const DEFAULT_SVG = (
  <svg viewBox="0 0 100 100" className="w-full h-full">
    <polygon points="50,2 98,50 50,98 2,50" fill="#fff" stroke="#999" strokeWidth="4" />
    <text x="50" y="60" textAnchor="middle" fontSize="40" fill="#999">?</text>
  </svg>
)

const SIZE_CLASSES = {
  sm: 'w-10 h-10',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
}

export default function GHSPictogram({
  code,
  name,
  description,
  size = 'md',
  showTooltip = true,
}: GHSPictogramProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  // Normaliser le code (GHS01, GHS-01, ghs01 -> GHS01)
  const normalizedCode = code.toUpperCase().replace(/-/g, '')

  // Récupérer les données du pictogramme
  const ghsInfo = GHS_DATA[normalizedCode]
  const displayName = name || ghsInfo?.name || 'Inconnu'
  const displayDescription = description || ghsInfo?.description || 'Pictogramme non reconnu'
  const svg = GHS_SVGS[normalizedCode] || DEFAULT_SVG

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className={`${SIZE_CLASSES[size]} cursor-pointer transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded`}
        onMouseEnter={() => showTooltip && setIsTooltipVisible(true)}
        onMouseLeave={() => setIsTooltipVisible(false)}
        onClick={() => showTooltip && setIsTooltipVisible(!isTooltipVisible)}
        aria-label={`${normalizedCode}: ${displayName}`}
      >
        {svg}
      </button>

      {/* Tooltip */}
      {showTooltip && isTooltipVisible && (
        <div
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg"
          role="tooltip"
        >
          <div className="font-bold text-red-400 mb-1">
            {normalizedCode} - {displayName}
          </div>
          <div className="text-gray-300 text-xs">
            {displayDescription}
          </div>
          {/* Flèche du tooltip */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  )
}

/**
 * Composant pour afficher une liste de pictogrammes
 * Accepte soit un tableau de codes (string[]) soit un tableau d'objets GHSPictogram
 */
interface GHSPictogramObject {
  code: string
  name?: string
  description?: string
}

export interface GHSPictogramListProps {
  codes?: string[]
  pictograms?: GHSPictogramObject[]
  size?: 'sm' | 'md' | 'lg'
}

export function GHSPictogramList({ codes, pictograms, size = 'md' }: GHSPictogramListProps) {
  // Supporter les deux formats: codes string[] ou pictograms object[]
  const items: GHSPictogramObject[] = pictograms || (codes ? codes.map(code => ({ code })) : [])

  if (!items || items.length === 0) {
    return (
      <span className="text-gray-500 italic text-sm">
        Aucun pictogramme associé
      </span>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <GHSPictogram
          key={item.code}
          code={item.code}
          name={item.name}
          description={item.description}
          size={size}
        />
      ))}
    </div>
  )
}
